// Everything the help layer draws over the current screen: the floating "?",
// the vote and away banners, the vote sheet, and the Help & session sheet.
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import { DUR, EASE_BEZIER, prefersReducedMotion } from '../../lib/anim-tokens';
import { useSessionHelp } from './context';
import SessionHelpButton from './SessionHelpButton';

// The sheet pulls in GMIcon (and so gsap) and every sub-view; none of it is
// needed until someone taps "?", so it stays out of the app shell.
// The vote and away UI load only when a vote is open or this seat is away,
// so a game with every flag off downloads none of it.
const SessionHelpSheet = lazy(() => import('./SessionHelpSheet'));
const VoteSheet = lazy(() => import('./VoteSheet'));
const VoteBanner = lazy(() => import('./VoteSheet').then((m) => ({ default: m.VoteBanner })));
const AwayBanner = lazy(() => import('./AwayBanner'));

// Below the "?" row, above the screen's own content.
const BANNER_STYLE = { top: 'max(calc(env(safe-area-inset-top) + 58px), 74px)' };

/** Banner entry/exit: essential, so reduced motion fades instead of sliding. */
function bannerMotion() {
  if (prefersReducedMotion()) {
    const t = { duration: DUR.fast };
    return { initial: { opacity: 0 }, animate: { opacity: 1, transition: t }, exit: { opacity: 0, transition: t } };
  }
  const t = { duration: DUR.med, ease: EASE_BEZIER.softOut };
  return {
    initial: { opacity: 0, y: -12 },
    animate: { opacity: 1, y: 0, transition: t },
    exit: { opacity: 0, y: -12, transition: { duration: DUR.fast } },
  };
}

export default function SessionHelpOverlay() {
  const ctx = useSessionHelp();
  const [voteOpen, setVoteOpen] = useState(false);
  const autoOpenedRef = useRef(new Set());
  const vote = ctx?.openVote;
  // Once the sheet has been opened it stays mounted, so closing it animates.
  const [sheetLoaded, setSheetLoaded] = useState(false);
  const helpOpen = Boolean(ctx?.helpOpen);
  useEffect(() => {
    if (helpOpen) setSheetLoaded(true);
  }, [helpOpen]);

  // Open the vote sheet once, by itself, for a phone that can vote and hasn't.
  useEffect(() => {
    if (!vote) return;
    if (!vote.can_vote || vote.my_ballot != null) return;
    if (autoOpenedRef.current.has(vote.id)) return;
    autoOpenedRef.current.add(vote.id);
    setVoteOpen(true);
  }, [vote]);

  if (!ctx?.sessionCode || !ctx.surface) return null;

  const votesOn = Boolean(ctx.features?.room_votes) && Boolean(ctx.me);
  const showVoteBanner = votesOn && Boolean(vote) && !voteOpen;
  const showAway = Boolean(ctx.me) && (ctx.tokenRevoked || (ctx.features?.room_active_seats && ctx.me.is_active === false));

  return (
    <>
      <SessionHelpButton floating />
      <div className="pointer-events-none fixed inset-x-4 z-[61] mx-auto flex max-w-lg flex-col gap-2" style={BANNER_STYLE}>
        <Suspense fallback={null}>
        <AnimatePresence initial={false}>
          {showAway && (
            <Motion.div key="away" {...bannerMotion()}>
              <AwayBanner />
            </Motion.div>
          )}
          {showVoteBanner && (
            <Motion.div key={`vote-${vote.id}`} {...bannerMotion()}>
              <VoteBanner onOpen={() => setVoteOpen(true)} />
            </Motion.div>
          )}
        </AnimatePresence>
        </Suspense>
      </div>
      {votesOn && (vote || voteOpen) && (
        <Suspense fallback={null}>
          <VoteSheet open={voteOpen} onClose={() => setVoteOpen(false)} />
        </Suspense>
      )}
      {ctx.visible && (helpOpen || sheetLoaded) && (
        <Suspense fallback={null}>
          <SessionHelpSheet />
        </Suspense>
      )}
    </>
  );
}
